import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, CircleDot } from "lucide-react";
import { api } from "@/lib/api";
import { Stat } from "@/pages/admin/Stat";

interface AutoSyncStatus {
  scheduler_running: boolean;
  tick_minutes: number;
  allowed_intervals: number[];
  scheduled_count: number;
  upcoming: Array<{
    account_id: string;
    platform: string;
    label: string;
    interval_hours: number;
    next_run_at: string | null;
    last_run_at: string | null;
    last_status: string | null;
  }>;
  errors_24h: Array<{ account_id: string; platform: string; at: string; message: string }>;
  errors_24h_count: number;
  checked_at: string;
}

/**
 * Small helper — humanise absolute ISO timestamps into a relative "in 5m" /
 * "3h ago" summary. Falls back to the raw ISO string if parsing fails so
 * the UI never shows an empty cell.
 */
const fmtRel = (iso?: string | null): string => {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = then - now;
    const abs = Math.abs(diff);
    const min = 60_000, hr = 3_600_000, day = 86_400_000;
    if (abs < min) return diff > 0 ? "in <1m" : "just now";
    if (abs < hr) return diff > 0 ? `in ${Math.round(abs / min)}m` : `${Math.round(abs / min)}m ago`;
    if (abs < day) return diff > 0 ? `in ${Math.round(abs / hr)}h` : `${Math.round(abs / hr)}h ago`;
    return diff > 0 ? `in ${Math.round(abs / day)}d` : `${Math.round(abs / day)}d ago`;
  } catch { return iso; }
};

/**
 * Cron-style sync jobs for OAuth-connected YouTube / Meta / Insta accounts.
 * Live-refreshes every 30s so the admin can watch the scheduler heartbeat
 * and see fresh errors as they land.
 */
export const AutoSyncStatusCard: React.FC = () => {
  const q = useQuery<AutoSyncStatus>({
    queryKey: ["admin-auto-sync-status"],
    queryFn: async () => (await api.get("/admin/auto-sync/status")).data,
    refetchInterval: 30_000,
  });
  const d = q.data;

  return (
    <div
      data-testid="admin-auto-sync-card"
      className="bg-white rounded-xl p-6 border border-[color:var(--sg-border)] mt-4"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[color:var(--sg-violet)]" />
          <h2 className="text-lg font-semibold text-[color:var(--sg-fg)]">Auto-syncing accounts</h2>
        </div>
        {d && (
          <div
            className="flex items-center gap-1.5 text-[11px] text-[color:var(--sg-muted)]"
            data-testid="auto-sync-scheduler-status"
          >
            <CircleDot className={`h-3 w-3 ${d.scheduler_running ? "text-emerald-500" : "text-rose-500"}`} />
            {d.scheduler_running ? `Scheduler live · ticks every ${d.tick_minutes}m` : "Scheduler stopped"}
          </div>
        )}
      </div>
      <p className="text-sm text-[color:var(--sg-muted)] mb-4">
        Cron-style sync jobs for OAuth-connected YouTube, Facebook and Instagram accounts.
      </p>

      {q.isLoading || !d ? (
        <div className="sg-skeleton h-32" />
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-3 text-xs">
            <Stat
              label="Scheduled accounts"
              value={String(d.scheduled_count)}
              testId="auto-sync-scheduled-count"
            />
            <Stat
              label="Errors · 24 h"
              value={String(d.errors_24h_count)}
              testId="auto-sync-errors-24h"
              danger={d.errors_24h_count > 0}
            />
            <Stat
              label="Next tick every"
              value={`${d.tick_minutes} min`}
              testId="auto-sync-tick"
            />
          </div>

          <div className="mt-5">
            <h3 className="text-xs font-semibold text-[color:var(--sg-fg)] mb-2 uppercase tracking-wide">
              Upcoming syncs {d.upcoming.length > 0 && `(${d.upcoming.length})`}
            </h3>
            {d.upcoming.length === 0 ? (
              <div className="text-xs text-[color:var(--sg-muted)] border border-dashed border-[color:var(--sg-border)] rounded-md p-3"
                    data-testid="auto-sync-upcoming-empty">
                No accounts are on a schedule yet. Enable auto-sync from the{" "}
                <a href="/platforms" className="underline hover:text-[color:var(--sg-fg)]">Platforms</a>{" "}
                page to see scheduled runs here.
              </div>
            ) : (
              <div className="border border-[color:var(--sg-border)] rounded-md divide-y divide-[color:var(--sg-border)] overflow-hidden">
                {d.upcoming.map((u) => (
                  <div key={u.account_id} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 py-2 text-xs"
                        data-testid={`auto-sync-upcoming-${u.account_id}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate text-[color:var(--sg-fg)]">{u.label}</div>
                      <div className="text-[10px] uppercase tracking-wide text-[color:var(--sg-muted)]">
                        {u.platform} · every {u.interval_hours}h
                      </div>
                    </div>
                    <div className="text-right text-[color:var(--sg-muted)]">
                      Next {fmtRel(u.next_run_at)}
                    </div>
                    <div className={`text-[10px] ${
                      u.last_status && !u.last_status.startsWith("ok") ? "text-amber-700" : "text-[color:var(--sg-muted)]"
                    }`}>
                      Last {fmtRel(u.last_run_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {d.errors_24h.length > 0 && (
            <div className="mt-5" data-testid="auto-sync-errors-list">
              <h3 className="text-xs font-semibold text-rose-700 mb-2 uppercase tracking-wide">
                Auto-sync errors · last 24 h
              </h3>
              <div className="border border-rose-200 rounded-md divide-y divide-rose-100 overflow-hidden bg-rose-50/40">
                {d.errors_24h.map((e, i) => (
                  <div key={i} className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2 text-xs"
                        data-testid={`auto-sync-error-${i}`}>
                    <div className="min-w-0">
                      <div className="font-medium text-[color:var(--sg-fg)] truncate">{e.platform} · {e.account_id}</div>
                      <div className="text-[11px] text-rose-800 line-clamp-2">{e.message}</div>
                    </div>
                    <div className="text-right text-[color:var(--sg-muted)]">{fmtRel(e.at)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
